import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ChevronDown, ChevronRight, Loader2, AlertTriangle, Package, Users, Check
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { normalizeCategory } from '../lib/categories';
import { StickerLine } from '../components/Stickers';

const PRODUCTION_DAYS = ['FRI', 'SAT', 'SUN', 'MON', 'TUES', 'WED', 'THURS'];

const formatWeight = (g, weightType) => {
  if (weightType === 'pieces' || weightType === 'count') {
    const val = g % 1 === 0 ? g : g.toFixed(1);
    return `${val} pcs`;
  }
  if (g >= 1000) return `${(g / 1000).toFixed(2)}kg`;
  return `${Math.round(g)}g`;
};

const ClientPackingTab = ({ selectedOrderId }) => {
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [clientProgress, setClientProgress] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [packingData, setPackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progressLoading, setProgressLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedComponents, setExpandedComponents] = useState(new Set());

  // Meal allocation state
  const [mealData, setMealData] = useState(null);
  const [mealLoading, setMealLoading] = useState(false);
  const [mealsOpen, setMealsOpen] = useState(true);
  const [clientsOpen, setClientsOpen] = useState(true);
  const [selectedMeal, setSelectedMeal] = useState(null); // dish_name of selected meal for right panel

  const toggleDay = (day) => {
    setSelectedDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day); else next.add(day);
      return next;
    });
  };

  // Load meal allocation data
  const loadMealAllocation = useCallback(async () => {
    if (selectedDays.size === 0 || !selectedOrderId) return;
    setMealLoading(true);
    try {
      const r = await axios.post(`${API}/production/${selectedOrderId}/meal-allocation`, {
        days: [...selectedDays]
      });
      setMealData(r.data);
    } catch (e) { /* ignore */ }
    finally { setMealLoading(false); }
  }, [selectedDays, selectedOrderId]);

  // Load client progress
  const loadProgress = useCallback(async () => {
    if (selectedDays.size === 0 || !selectedOrderId) return;
    setProgressLoading(true);
    try {
      const dayStr = [...selectedDays].join(',');
      const r = await axios.get(`${API}/production/${selectedOrderId}/client-packing/progress?days=${dayStr}`);
      setClientProgress(r.data.clients || []);
    } catch (e) { /* ignore */ }
    finally { setProgressLoading(false); }
  }, [selectedDays, selectedOrderId]);

  useEffect(() => {
    if (selectedDays.size > 0) {
      loadMealAllocation();
      loadProgress();
      setSelectedClient('');
      setPackingData(null);
    } else {
      setMealData(null);
      setClientProgress([]);
    }
  }, [selectedDays, loadMealAllocation, loadProgress]);

  // Load packing data for selected client
  const loadClientPacking = useCallback(async (clientName) => {
    if (!clientName || selectedDays.size === 0 || !selectedOrderId) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/production/${selectedOrderId}/client-packing`, {
        days: [...selectedDays], client_name: clientName
      });
      setPackingData(r.data);
      const cats = new Set(r.data.items.map(i => normalizeCategory(i.category)));
      setExpandedCategories(cats);
      const autoExpand = new Set();
      const pt = r.data.client?.pack_type || 'Individual';
      if (pt === 'Sharing' || pt === 'Multi') {
        r.data.items.forEach(i => { if (i.has_packing_sheet) autoExpand.add(i.item_key); });
      }
      setExpandedComponents(autoExpand);
    } catch (e) { toast.error('Failed to load packing data'); }
    finally { setLoading(false); }
  }, [selectedDays, selectedOrderId]);

  const selectClient = (clientName) => {
    setSelectedClient(clientName);
    setSelectedMeal(null);
    loadClientPacking(clientName);
  };

  const selectMeal = (dishName) => {
    setSelectedMeal(dishName);
    setSelectedClient('');
    setPackingData(null);
  };

  // Toggle checkbox for meal allocation (ticks both meal view and client view)
  const toggleMealItem = async (clientName, itemKey, currentChecked) => {
    if (!selectedOrderId) return;

    // Optimistic update in meal data
    setMealData(prev => {
      if (!prev) return prev;
      const updated = { ...prev, meals: prev.meals.map(m => ({
        ...m,
        clients: m.clients.map(c =>
          c.client_name === clientName && c.item_key === itemKey
            ? { ...c, checked: !currentChecked }
            : c
        ),
        allocated_portions: m.clients.reduce((sum, c) => {
          const isThis = c.client_name === clientName && c.item_key === itemKey;
          const checked = isThis ? !currentChecked : c.checked;
          return sum + (checked ? c.quantity : 0);
        }, 0),
      }))};
      updated.meals = updated.meals.map(m => ({ ...m, fully_allocated: m.allocated_portions >= m.total_portions }));
      updated.fully_allocated_count = updated.meals.filter(m => m.fully_allocated).length;
      return updated;
    });

    // Optimistic update in client packing (if this client is selected)
    if (packingData && packingData.client?.name === clientName) {
      setPackingData(prev => {
        if (!prev) return prev;
        const newChecked = { ...prev.checked_items, [itemKey]: !currentChecked };
        const newCount = prev.items.filter(i => newChecked[i.item_key]).length;
        return { ...prev, checked_items: newChecked, checked_count: newCount };
      });
    }

    // Update client progress
    setClientProgress(prev => prev.map(c =>
      c.client_name === clientName
        ? { ...c, checked_count: c.checked_count + (currentChecked ? -1 : 1), complete: (c.checked_count + (currentChecked ? -1 : 1)) >= c.total_items }
        : c
    ));

    // Save to DB
    try {
      await axios.put(`${API}/production/${selectedOrderId}/client-packing/status`, {
        client_name: clientName, item_key: itemKey, checked: !currentChecked
      });
    } catch (e) {
      toast.error('Failed to save');
      // Revert on error
      loadMealAllocation();
      loadProgress();
      if (packingData && packingData.client?.name === clientName) loadClientPacking(clientName);
    }
  };

  // Toggle from client view (also updates meal view)
  const toggleClientItem = async (itemKey, currentChecked) => {
    if (!selectedOrderId || !selectedClient) return;
    await toggleMealItem(selectedClient, itemKey, currentChecked);
  };

  const toggleComponent = (itemKey) => {
    setExpandedComponents(prev => {
      const next = new Set(prev);
      if (next.has(itemKey)) next.delete(itemKey); else next.add(itemKey);
      return next;
    });
  };

  const filteredClients = clientProgress.filter(c =>
    !clientSearch || c.client_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    (c.key || '').toLowerCase().includes(clientSearch.toLowerCase())
  );

  // Group client packing items by category
  const groupedItems = {};
  if (packingData) {
    for (const item of packingData.items) {
      const cat = normalizeCategory(item.category) || 'Other';
      if (!groupedItems[cat]) groupedItems[cat] = [];
      groupedItems[cat].push(item);
    }
  }

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const checkedItems = packingData?.checked_items || {};
  const checkedCount = packingData?.checked_count || 0;
  const totalItems = packingData?.total_items || 0;
  const progressPct = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;

  // Meal section stats
  const mealTotal = mealData?.total_meals || 0;
  const mealAllocated = mealData?.fully_allocated_count || 0;
  const mealPct = mealTotal > 0 ? Math.round((mealAllocated / mealTotal) * 100) : 0;

  // Client section stats
  const clientTotal = clientProgress.length;
  const clientComplete = clientProgress.filter(c => c.complete).length;
  const clientPct = clientTotal > 0 ? Math.round((clientComplete / clientTotal) * 100) : 0;

  // Group meals by category
  const mealsByCategory = {};
  if (mealData) {
    for (const meal of mealData.meals) {
      const cat = normalizeCategory(meal.category) || 'Other';
      if (!mealsByCategory[cat]) mealsByCategory[cat] = [];
      mealsByCategory[cat].push(meal);
    }
  }

  // Get selected meal data for right panel
  const selectedMealData = selectedMeal && mealData
    ? mealData.meals.find(m => m.dish_name === selectedMeal)
    : null;

  return (
    <div data-testid="client-packing-tab">
      {/* Day Selection */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mr-1">Production Days:</span>
        {PRODUCTION_DAYS.map(d => (
          <button key={d} onClick={() => toggleDay(d)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${selectedDays.has(d) ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
            data-testid={`cp-day-${d}`}>{d}</button>
        ))}
      </div>

      {selectedDays.size === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Package className="w-16 h-16 mx-auto mb-3 opacity-20" />
          <p className="text-sm">Select production days to begin packing</p>
        </div>
      )}

      {selectedDays.size > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* LEFT: Meals + Clients Panels */}
          <div className="lg:col-span-4 space-y-3">
            {/* === MEALS SECTION === */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-3 border-b border-gray-200 cursor-pointer" onClick={() => setMealsOpen(!mealsOpen)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {mealsOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                    <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Meals</span>
                    <span className="text-xs text-gray-400 tabular-nums">{mealAllocated}/{mealTotal}</span>
                  </div>
                  {mealLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                </div>
                <div className="mt-2 w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-300"
                    style={{ width: `${mealPct}%`, backgroundColor: mealPct === 100 ? '#22c55e' : 'var(--color-primary)' }} />
                </div>
              </div>
              {mealsOpen && mealData && (
                <div className="max-h-[45vh] overflow-y-auto">
                  {Object.entries(mealsByCategory).map(([category, meals]) => (
                    <div key={category}>
                      <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[10px] font-bold text-gray-500 uppercase tracking-wider">{category}</div>
                      {meals.map(meal => {
                        return (
                          <div key={meal.dish_name} className={`border-b border-gray-50 ${meal.fully_allocated ? 'bg-green-50/50' : ''} ${selectedMeal === meal.dish_name ? 'bg-[var(--color-primary-50)] border-l-2 border-l-[var(--color-primary)]' : ''}`}>
                            <div className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
                              onClick={() => selectMeal(meal.dish_name)}
                              data-testid={`meal-${meal.dish_name}`}>
                              {meal.fully_allocated
                                ? <Check className="w-5 h-5 text-green-600 shrink-0" />
                                : <div className="w-5" />
                              }
                              <div className="flex-1 min-w-0">
                                <div className={`font-medium text-sm leading-tight ${meal.fully_allocated ? 'text-green-700' : ''}`}>{meal.dish_name}</div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {Object.entries(meal.sizes).map(([sz, qty]) => (
                                    <span key={sz} className="text-[10px] text-gray-400">{sz}: {qty}</span>
                                  ))}
                                </div>
                              </div>
                              <span className={`text-lg font-bold tabular-nums shrink-0 ${meal.fully_allocated ? 'text-green-600' : 'text-gray-900'}`}>
                                {meal.allocated_portions}/{meal.total_portions}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* === CLIENTS SECTION === */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-3 border-b border-gray-200 cursor-pointer" onClick={() => setClientsOpen(!clientsOpen)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {clientsOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                    <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Clients</span>
                    <span className="text-xs text-gray-400 tabular-nums">{clientComplete}/{clientTotal}</span>
                  </div>
                  {progressLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                </div>
                <div className="mt-2 w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-300"
                    style={{ width: `${clientPct}%`, backgroundColor: clientPct === 100 ? '#22c55e' : 'var(--color-primary)' }} />
                </div>
              </div>
              {clientsOpen && (
                <div>
                  <div className="p-2 border-b border-gray-100">
                    <Input placeholder="Search clients..." value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="h-8 text-xs" data-testid="cp-client-search" />
                  </div>
                  <div className="max-h-[35vh] overflow-y-auto">
                    {filteredClients.length === 0 ? (
                      <div className="p-6 text-center text-gray-400 text-sm">No clients</div>
                    ) : filteredClients.map(c => {
                      const isSelected = c.client_name === selectedClient;
                      const isDone = c.complete;
                      return (
                        <div key={c.client_name}
                          onClick={() => selectClient(c.client_name)}
                          className={`px-3 py-2.5 border-b border-gray-50 cursor-pointer transition-colors ${isSelected ? 'bg-[var(--color-primary-50)] border-l-2 border-l-[var(--color-primary)]' : 'hover:bg-gray-50'}`}
                          data-testid={`cp-client-${c.client_name}`}>
                          <div className="flex items-center justify-between">
                            <div className="min-w-0">
                              <div className="font-medium text-sm truncate">{c.client_name}</div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                {c.key && <span className="px-2 py-0.5 bg-[var(--color-primary)] text-white text-xs font-bold rounded">{c.key}</span>}
                                <span className="text-[10px] text-gray-400">{c.pack_type}</span>
                              </div>
                            </div>
                            <div className="shrink-0 ml-2">
                              {isDone ? (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full">
                                  <Check className="w-3 h-3" /> Done
                                </span>
                              ) : (
                                <span className="text-[10px] text-gray-400 tabular-nums">{c.checked_count}/{c.total_items}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Meal Detail OR Client Packing Detail */}
          <div className="lg:col-span-8">
            {/* === MEAL DETAIL VIEW === */}
            {selectedMealData ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-[var(--color-primary)]" />
                    <div>
                      <h2 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{selectedMealData.dish_name}</h2>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="badge-pill badge-teal text-[10px]">{normalizeCategory(selectedMealData.category)}</span>
                        {Object.entries(selectedMealData.sizes).map(([sz, qty]) => (
                          <span key={sz} className="badge-pill badge-gray text-[10px]">{sz}: {qty}</span>
                        ))}
                        {selectedMealData.is_vegetarian && <span className="text-green-600 text-xs font-bold">(v)</span>}
                      </div>
                      <StickerLine stickers={selectedMealData.stickers} testId="cp-meal-stickers" className="mt-2" />
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold tabular-nums">{selectedMealData.allocated_portions}/{selectedMealData.total_portions} allocated</div>
                    <div className="w-32 h-2 bg-gray-200 rounded-full mt-1 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-300"
                        style={{ width: `${selectedMealData.total_portions > 0 ? Math.round((selectedMealData.allocated_portions / selectedMealData.total_portions) * 100) : 0}%`, backgroundColor: selectedMealData.fully_allocated ? '#22c55e' : 'var(--color-primary)' }} />
                    </div>
                  </div>
                </div>
                {/* Client allocation list */}
                <div className="space-y-2">
                  {selectedMealData.clients.map((c, ci) => (
                    <div key={`${c.client_name}-${c.item_key}-${ci}`}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${c.checked ? 'bg-green-50 border-green-200' : c.allergy_notes ? 'border-red-300 bg-red-50/30' : 'bg-white border-gray-200'}`}>
                      <Checkbox checked={c.checked}
                        onCheckedChange={() => toggleMealItem(c.client_name, c.item_key, c.checked)}
                        className="shrink-0 w-6 h-6" />
                      {c.allergy_notes && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[9px] font-bold rounded shrink-0">⚠️ {c.allergy_notes}</span>
                      )}
                      <div className="flex-1 min-w-0">
                        <span className={`font-semibold text-base ${c.checked ? 'line-through text-gray-400' : ''}`}>{c.client_name}</span>
                      </div>
                      <span className="px-3 py-1 bg-[var(--color-primary)] text-white text-base font-bold rounded shrink-0">{c.key}</span>
                      <span className="badge-pill badge-gray text-xs shrink-0">{c.meal_size}</span>
                      <span className={`text-2xl font-bold tabular-nums shrink-0 ${c.checked ? 'text-gray-300' : 'text-gray-900'}`}>x{c.quantity}</span>
                    </div>
                  ))}
                </div>
                {selectedMealData.fully_allocated && (
                  <div className="text-center py-6 bg-green-50 border border-green-200 rounded-lg mt-4">
                    <Check className="w-8 h-8 mx-auto text-green-600 mb-2" />
                    <div className="font-semibold text-green-700">All {selectedMealData.total_portions} portions of {selectedMealData.dish_name} allocated</div>
                  </div>
                )}
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" /></div>
            ) : packingData ? (
              <div>
                {/* Client Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-[var(--color-primary)]" />
                    <div>
                      <div className="flex items-center gap-3">
                        <h2 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{packingData.client.name}</h2>
                        {packingData.client.key && (
                          <span className="px-3 py-1 bg-[var(--color-primary)] text-white text-lg font-bold rounded" data-testid="cp-client-key">{packingData.client.key}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="badge-pill badge-gray text-[10px]">{packingData.client.size}</span>
                        <span className="badge-pill badge-gray text-[10px]">{packingData.client.pack_type}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold tabular-nums">{checkedCount}/{totalItems} packed</div>
                    <div className="w-32 h-2 bg-gray-200 rounded-full mt-1 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-300"
                        style={{ width: `${progressPct}%`, backgroundColor: progressPct === 100 ? '#22c55e' : 'var(--color-primary)' }} />
                    </div>
                  </div>
                </div>

                {/* Items grouped by category */}
                {Object.entries(groupedItems).map(([category, items]) => {
                  const catExpanded = expandedCategories.has(category);
                  const catChecked = items.filter(i => checkedItems[i.item_key]).length;
                  return (
                    <div key={category} className="mb-3">
                      <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg cursor-pointer"
                        onClick={() => toggleCategory(category)} data-testid={`cp-cat-${category}`}>
                        {catExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                        <span className="text-xs font-bold text-gray-600 uppercase tracking-wider flex-1">{category}</span>
                        <span className="text-[10px] text-gray-400 tabular-nums">{catChecked}/{items.length}</span>
                      </div>
                      {catExpanded && (
                        <div className="mt-1 space-y-1">
                          {items.map(item => {
                            const isChecked = !!checkedItems[item.item_key];
                            const hasAllergy = !!item.allergy_notes;
                            const isSharing = (item.pack_type === 'Sharing' || item.pack_type === 'Multi') && item.has_packing_sheet;
                            const compExpanded = expandedComponents.has(item.item_key);
                            const hasComponents = item.components.length > 0;

                            return (
                              <div key={item.item_key}
                                className={`border rounded-lg overflow-hidden transition-colors ${isChecked ? 'bg-green-50/50 border-green-200' : hasAllergy ? 'border-red-300 bg-red-50/30' : 'bg-white border-gray-200'}`}
                                data-testid={`cp-item-${item.item_key}`}>
                                {hasAllergy && (
                                  <div className="flex items-center gap-2 px-4 py-2 bg-red-100 border-b border-red-200">
                                    <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                                    <span className="text-sm font-bold text-red-700">{item.allergy_notes}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-3 px-4 py-3">
                                  <Checkbox checked={isChecked}
                                    onCheckedChange={() => toggleClientItem(item.item_key, isChecked)}
                                    className="shrink-0 w-5 h-5" />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className={`font-semibold text-base ${isChecked ? 'line-through text-gray-400' : ''}`}>
                                        {item.dish_name}
                                      </span>
                                      {item.is_vegetarian && <span className="text-green-600 text-xs font-bold">(v)</span>}
                                    </div>
                                    <span className="badge-pill badge-gray text-[9px] mt-0.5">{item.meal_size}</span>
                                  </div>
                                  <span className={`text-2xl font-bold tabular-nums ${isChecked ? 'text-gray-300' : 'text-gray-900'}`}>
                                    x{item.quantity}
                                  </span>
                                </div>
                                <StickerLine stickers={item.stickers} testId={`cp-item-stickers-${item.item_key}`} className={`mx-4 mb-2 ${isChecked ? 'opacity-50' : ''}`} />
                                {hasComponents && (
                                  <div className={`${isChecked ? 'opacity-40' : ''}`}>
                                    {!isSharing && (
                                      <div className="px-4 pb-1">
                                        <button onClick={() => toggleComponent(item.item_key)}
                                          className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600">
                                          {compExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                          {compExpanded ? 'Hide' : 'Show'} components ({item.components.length})
                                        </button>
                                      </div>
                                    )}
                                    {(isSharing || compExpanded) && (
                                      <div className="px-4 pb-3 ml-8">
                                        {item.components.map(c => (
                                          <div key={c.component_name} className="flex items-center justify-between py-1.5 border-t border-gray-100 first:border-t-0">
                                            <div className="flex items-center gap-2">
                                              <span className="text-sm text-gray-700">{c.component_name}</span>
                                              {c.is_group && <span className="text-[8px] text-blue-600 font-bold uppercase">Grouped</span>}
                                              {c.packaging && <span className="text-[9px] text-gray-400">— {c.packaging}</span>}
                                            </div>
                                            <span className="text-sm tabular-nums font-semibold text-gray-800">{formatWeight(c.total_weight, c.weight_type)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                {progressPct === 100 && (
                  <div className="text-center py-6 bg-green-50 border border-green-200 rounded-lg mt-4" data-testid="cp-complete">
                    <Check className="w-8 h-8 mx-auto text-green-600 mb-2" />
                    <div className="font-semibold text-green-700">All items packed for {packingData.client.name}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-20 text-gray-400" style={{ minHeight: '400px' }}>
                <div className="text-center">
                  <Package className="w-16 h-16 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">Select a meal or client to view packing details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPackingTab;
